import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SortableTable from '../shared/sortable_table';

const AdminGiftCodes = ({ gift_codes: initialGiftCodes = [] }) => {
  const [giftCodes, setGiftCodes] = useState(initialGiftCodes);
  const [flashMessage, setFlashMessage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedGiftCode, setSelectedGiftCode] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const [emailFilter, setEmailFilter] = useState('');

  const showFlash = (message, type = 'success') => {
    setFlashMessage({ message, type });
    setTimeout(() => setFlashMessage(null), 5000);
  };

  // Filter gift codes by email or gift code
  const filteredGiftCodes = useMemo(() => {
    if (!emailFilter.trim()) return giftCodes;
    const filter = emailFilter.toLowerCase().trim();
    return giftCodes.filter(gc =>
      (gc.buyers_email && gc.buyers_email.toLowerCase().includes(filter)) ||
      (gc.intended_email && gc.intended_email.toLowerCase().includes(filter)) ||
      (gc.gift_code && gc.gift_code.toLowerCase().includes(filter))
    );
  }, [giftCodes, emailFilter]);

  const handleResendEmails = async (giftCodeId) => {
    try {
      const response = await fetch(`/admin/gift_codes/${giftCodeId}/resend_emails`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });
      const data = await response.json();
      if (data.success) {
        showFlash(data.message);
      } else {
        showFlash(data.message, 'error');
      }
    } catch (error) {
      showFlash('Failed to resend emails', 'error');
    }
  };

  const openApplyModal = (giftCode) => {
    setSelectedGiftCode(giftCode);
    setIsModalOpen(true);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedAccount(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedGiftCode(null);
    setSearchQuery('');
    setSearchResults([]);
    setSelectedAccount(null);
  };

  const searchAccounts = useCallback(async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const response = await fetch(`/admin/gift_codes/search_accounts?query=${encodeURIComponent(query)}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        }
      });
      const data = await response.json();
      setSearchResults(data.accounts || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery) {
        searchAccounts(searchQuery);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, searchAccounts]);

  const handleApplyToAccount = async () => {
    if (!selectedAccount || !selectedGiftCode) return;

    setIsApplying(true);
    try {
      const response = await fetch(`/admin/gift_codes/${selectedGiftCode.id}/apply_to_account`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ account_id: selectedAccount.id })
      });
      const data = await response.json();
      if (data.success) {
        showFlash(data.message);
        // Update the gift code in the list
        setGiftCodes(prev => prev.map(gc =>
          gc.id === selectedGiftCode.id ? data.gift_code : gc
        ));
        closeModal();
      } else {
        showFlash(data.message, 'error');
      }
    } catch (error) {
      showFlash('Failed to apply gift code', 'error');
    } finally {
      setIsApplying(false);
    }
  };

  const formatAmount = (cents) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  // Render email with optional link to account
  const renderEmail = (email, accountSlug) => {
    if (accountSlug) {
      return (
        <a
          href={`/accounts/${accountSlug}/edit`}
          style={{ color: '#2563eb', textDecoration: 'none' }}
          onClick={(e) => e.stopPropagation()}
        >
          {email}
        </a>
      );
    }
    return email;
  };

  const rowTemplates = [
    {
      header_label: 'Gift Code',
      sort_by_property: 'gift_code',
      render_cell: (row) => (
        <a
          href={`/rails_admin/gift_code/${row.id}/edit`}
          style={{ fontFamily: 'monospace', fontWeight: 500, color: '#374151', textDecoration: 'none' }}
          title="Edit in Rails Admin"
        >
          {row.gift_code}
        </a>
      )
    },
    {
      header_label: 'Buyer',
      sort_by_property: 'buyers_name',
      render_cell: (row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.buyers_name}</div>
          <div style={{ fontSize: '12px', color: row.buyers_account_slug ? '#2563eb' : '#6b7280' }}>
            {renderEmail(row.buyers_email, row.buyers_account_slug)}
          </div>
        </div>
      )
    },
    {
      header_label: 'Recipient',
      sort_by_property: 'intended_name',
      render_cell: (row) => (
        <div>
          <div style={{ fontWeight: 500 }}>{row.intended_name}</div>
          <div style={{ fontSize: '12px', color: row.intended_account_slug ? '#2563eb' : '#6b7280' }}>
            {renderEmail(row.intended_email, row.intended_account_slug)}
          </div>
        </div>
      )
    },
    {
      header_label: 'Amount',
      sort_by_property: 'amount',
      render_cell: (row) => (
        <span style={{ fontWeight: 500 }}>{formatAmount(row.amount)}</span>
      ),
      sort_function: (a, b) => a.amount - b.amount
    },
    {
      header_label: 'Status',
      sort_by_property: 'is_redeemed',
      render_cell: (row) => (
        <div>
          {row.is_redeemed ? (
            <div>
              <span style={{
                display: 'inline-block',
                padding: '2px 8px',
                backgroundColor: '#dcfce7',
                color: '#166534',
                borderRadius: '9999px',
                fontSize: '12px',
                fontWeight: 500
              }}>
                Redeemed
              </span>
              {row.redeemed_account && (
                <div style={{ marginTop: '4px', fontSize: '12px' }}>
                  <a
                    href={`/accounts/${row.redeemed_account.slug}/edit`}
                    style={{ color: '#2563eb', textDecoration: 'none' }}
                  >
                    {row.redeemed_account.email}
                  </a>
                </div>
              )}
            </div>
          ) : (
            <span style={{
              display: 'inline-block',
              padding: '2px 8px',
              backgroundColor: '#fef3c7',
              color: '#92400e',
              borderRadius: '9999px',
              fontSize: '12px',
              fontWeight: 500
            }}>
              Unredeemed
            </span>
          )}
        </div>
      ),
      sort_function: (a, b) => (a.is_redeemed === b.is_redeemed ? 0 : a.is_redeemed ? 1 : -1)
    },
    {
      header_label: 'Created',
      sort_by_property: 'created_at',
      render_cell: (row) => (
        <span style={{ fontSize: '13px' }}>{row.created_at}</span>
      ),
      sort_function: (a, b) => a.created_at_timestamp - b.created_at_timestamp
    },
    {
      header_label: 'Actions',
      sort_by_property: 'actions',
      render_cell: (row) => (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => handleResendEmails(row.id)}
            style={{
              padding: '6px 12px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Resend Emails
          </button>
          {!row.is_redeemed && (
            <button
              onClick={() => openApplyModal(row)}
              style={{
                padding: '6px 12px',
                backgroundColor: 'white',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '12px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Apply to Account
            </button>
          )}
        </div>
      ),
      sort_function: () => 0
    }
  ];

  return (
    <div className="admin__page">
      <div className="admin__page-header">
        <h1 className="admin__page-title">Gift Codes</h1>
        <div className="admin__page-subtitle">
          {filteredGiftCodes.length === giftCodes.length
            ? `${giftCodes.length} total gift codes`
            : `Showing ${filteredGiftCodes.length} of ${giftCodes.length} gift codes`
          }
        </div>
      </div>

      {flashMessage && (
        <div
          style={{
            padding: '12px 16px',
            marginBottom: '16px',
            borderRadius: '8px',
            backgroundColor: flashMessage.type === 'error' ? '#fef2f2' : '#f0fdf4',
            color: flashMessage.type === 'error' ? '#991b1b' : '#166534',
            border: `1px solid ${flashMessage.type === 'error' ? '#fecaca' : '#bbf7d0'}`
          }}
        >
          {flashMessage.message}
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <div style={{ position: 'relative', maxWidth: '400px' }}>
          <input
            type="text"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            placeholder="Search by email or gift code..."
            style={{
              width: '100%',
              padding: '10px 12px',
              paddingRight: emailFilter ? '36px' : '12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px',
              boxSizing: 'border-box'
            }}
          />
          {emailFilter && (
            <button
              onClick={() => setEmailFilter('')}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#9ca3af',
                fontSize: '18px',
                padding: '4px',
                lineHeight: 1
              }}
            >
              &times;
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: '16px' }}>
        <SortableTable
          rows_data={filteredGiftCodes}
          row_templates={rowTemplates}
          initial_sort_by="created_at"
        />
      </div>

      {isModalOpen && selectedGiftCode && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '100%',
              maxWidth: '500px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
          >
            <h2 style={{ margin: '0 0 16px', fontSize: '18px', fontWeight: 600 }}>
              Apply Gift Code to Existing Account
            </h2>

            <div style={{
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              marginBottom: '16px'
            }}>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>Gift Code</div>
              <div style={{ fontFamily: 'monospace', fontWeight: 500, fontSize: '16px' }}>
                {selectedGiftCode.gift_code}
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
                Amount: <strong>{formatAmount(selectedGiftCode.amount)}</strong>
              </div>
              <div style={{ fontSize: '14px', color: '#6b7280' }}>
                Intended for: <strong>{selectedGiftCode.intended_name}</strong> ({selectedGiftCode.intended_email})
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                Search for an active account
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email or name..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            {isSearching && (
              <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '12px' }}>
                Searching...
              </div>
            )}

            {searchResults.length > 0 && (
              <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                marginBottom: '16px',
                maxHeight: '200px',
                overflow: 'auto'
              }}>
                {searchResults.map((account) => (
                  <div
                    key={account.id}
                    onClick={() => setSelectedAccount(account)}
                    style={{
                      padding: '12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #e5e7eb',
                      backgroundColor: selectedAccount?.id === account.id ? '#eff6ff' : 'white',
                      transition: 'background-color 0.15s'
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{account.name || 'No name'}</div>
                    <div style={{ fontSize: '13px', color: '#6b7280' }}>{account.email}</div>
                  </div>
                ))}
              </div>
            )}

            {searchQuery.length > 0 && searchQuery.length < 2 && (
              <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '12px' }}>
                Type at least 2 characters to search
              </div>
            )}

            {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
              <div style={{ color: '#6b7280', fontSize: '14px', marginBottom: '12px' }}>
                No active accounts found matching "{searchQuery}"
              </div>
            )}

            {selectedAccount && (
              <div style={{
                padding: '12px',
                backgroundColor: '#eff6ff',
                borderRadius: '8px',
                marginBottom: '16px',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{ fontWeight: 500, color: '#1e40af' }}>Selected Account</div>
                <div>{selectedAccount.name || 'No name'}</div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>{selectedAccount.email}</div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeModal}
                style={{
                  padding: '10px 16px',
                  backgroundColor: 'white',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleApplyToAccount}
                disabled={!selectedAccount || isApplying}
                style={{
                  padding: '10px 16px',
                  backgroundColor: selectedAccount && !isApplying ? '#3b82f6' : '#9ca3af',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: selectedAccount && !isApplying ? 'pointer' : 'not-allowed'
                }}
              >
                {isApplying ? 'Applying...' : `Apply ${formatAmount(selectedGiftCode.amount)} Credit`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGiftCodes;
